/**
 * Prima di tutto il resto: Termini di servizio e informativa sulla privacy.
 *
 * Gli account li apriamo noi dopo una chiamata, e nessuno passa da un modulo
 * di registrazione: senza questa schermata i Termini non li accettava mai
 * nessuno. Si chiedono al primo accesso, e di nuovo quando cambiano (la
 * versione è `VERSIONE_TERMINI`). La casella parte vuota: un consenso già
 * spuntato non è un consenso.
 */

import { useState } from "react";
import { LogOut } from "lucide-react";
import { VERSIONE_TERMINI } from "@otto/shared";
import { api, setToken } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { Pill } from "../ui/primitives.tsx";
import { Logo } from "./Accesso.tsx";

const TERMINI = "https://ottoguest.com/termini";
const PRIVACY = "https://ottoguest.com/privacy";

export function AccettaTermini({
  nome,
  onAccettati,
}: {
  nome: string | null;
  onAccettati: () => Promise<unknown>;
}) {
  const [spuntata, setSpuntata] = useState(false);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const continua = async () => {
    setInvio(true);
    setErrore(null);
    try {
      await api.accettaTermini(VERSIONE_TERMINI);
      await onAccettati();
    } catch (e) {
      setErrore(messaggioDi(e));
      setInvio(false);
    }
  };

  const esci = () => {
    setToken(null);
    window.location.reload();
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-surface px-6 py-8 sm:px-12">
      <div className="flex items-center gap-2">
        <Logo />
        <span className="text-[19px] font-extrabold tracking-tight text-ink">
          Otto
        </span>
      </div>

      <div className="my-auto w-full max-w-[480px] self-center py-12">
        <div className="animate-rise">
          <h1 className="text-[28px] font-extrabold leading-tight tracking-tight text-ink">
            {nome ? `Ciao ${nome}.` : "Ciao."}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
            Prima di cominciare, leggi come funziona il servizio e come
            trattiamo i dati, tuoi e dei tuoi ospiti.
          </p>

          <div className="mt-5 rounded-card border border-line bg-surface-muted px-4 py-3.5 text-[13.5px] leading-relaxed text-ink-soft">
            In breve: Otto accede al tuo account Guesty con le chiavi che gli
            dai tu, e lavora per conto tuo; non modifica niente senza la tua
            approvazione; i dati restano tuoi e li puoi scaricare o cancellare
            quando vuoi.
          </div>

          <label className="mt-6 flex cursor-pointer items-start gap-3 text-[14.5px] leading-relaxed text-ink">
            <input
              type="checkbox"
              checked={spuntata}
              onChange={(e) => setSpuntata(e.target.checked)}
              className="mt-1 size-4 shrink-0 accent-ink"
            />
            <span>
              Ho letto e accetto i{" "}
              <a
                href={TERMINI}
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline underline-offset-2"
              >
                Termini di servizio
              </a>{" "}
              e l'
              <a
                href={PRIVACY}
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline underline-offset-2"
              >
                Informativa sulla privacy
              </a>
              .
            </span>
          </label>

          <div className="mt-7 flex flex-col gap-2.5">
            <Pill
              variant="primary"
              size="md"
              block
              disabled={!spuntata}
              loading={invio}
              onClick={() => void continua()}
            >
              Continua
            </Pill>
            <Pill
              variant="ghost"
              size="md"
              block
              icon={<LogOut size={15} />}
              onClick={esci}
            >
              Esci
            </Pill>
          </div>

          {errore ? (
            <p className="mt-4 text-[13.5px] font-semibold text-danger">
              {errore}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
